// Owner: shell-nav (plan §9.2, shell-ia F-20). Distinct document titles: `<Entity> · <Section> —
// <Workspace>`. The shell derives a title for every route from what it already knows (rail labels,
// settings pages, the titles of recently opened items); a screen that knows better calls
// `useDocumentTitle({ entity, section })` (the last mounted one wins).
import { useEffect, useSyncExternalStore } from 'react';
import type { NavDTO, RecentItem } from '@dataroom/core';
import { type MessageKey, t } from '../lib/i18n';

export type TitleParts = {
  entity?: string | null | undefined;
  section?: string | null | undefined;
  /** The screen is an error or "not found" state: nothing the member is working on (the Copilot has no screen chip). */
  state?: 'error' | undefined;
};

export const PRODUCT_NAME = 'Dataroom';

/** `Acme Corp · Companies — Example workspace`. */
export function composeTitle(parts: TitleParts, workspace: string | null | undefined): string {
  const head = [parts.entity, parts.section].filter((p): p is string => Boolean(p && p.trim())).join(' · ');
  const tail = workspace?.trim() || PRODUCT_NAME;
  return head ? `${head} — ${tail}` : tail;
}

// -- overrides from screens ----------------------------------------------------------------------------

let seq = 0;
let overrides: Array<{ id: number; parts: TitleParts }> = [];
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of [...listeners]) l();
};

/** A screen's own title (record pages, editors): wins over the shell's derived title while mounted. */
export function useDocumentTitle(parts: TitleParts | null): void {
  const entity = parts?.entity ?? null;
  const section = parts?.section ?? null;
  const state = parts?.state;
  const active = parts !== null;
  useEffect(() => {
    if (!active) return;
    const id = ++seq;
    overrides = [...overrides, { id, parts: { entity, section, ...(state ? { state } : {}) } }];
    emit();
    return () => {
      overrides = overrides.filter((o) => o.id !== id);
      emit();
    };
  }, [active, entity, section, state]);
}

export function useTitleOverride(): TitleParts | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => overrides.at(-1)?.parts ?? null,
    () => null,
  );
}

// -- derived titles ----------------------------------------------------------------------------------------

export type TitleLookups = {
  nav?: NavDTO | undefined;
  recent?: readonly RecentItem[] | undefined;
  /** The settings or Account page a path belongs to (account-settings' model, loaded on demand). */
  settingsPage?: ((pathname: string) => { label: string; account: boolean } | null) | undefined;
};

const label = (key: MessageKey) => t(key);

/** The title of a route from what the shell knows (null parts = the workspace name alone). */
export function routeTitle(
  pathname: string,
  search: Record<string, unknown>,
  lookups: TitleLookups,
): TitleParts {
  const p = pathname.replace(/\/+$/, '') || '/';
  const objects = lookups.nav?.apps.flatMap((a) => a.objects) ?? [];
  // Titles of items the shell already knows: opened recently or pinned.
  const recent: ReadonlyArray<Pick<RecentItem, 'id' | 'kind' | 'objectKey' | 'href' | 'label'>> = [
    ...(lookups.recent ?? []),
    ...(lookups.nav?.pinned ?? []),
  ];
  const recentById = (id: string) => recent.find((r) => r.id === id);
  const objectLabel = (key: string) => objects.find((o) => o.key === key)?.label ?? null;

  if (p === '/') return { entity: label('shell.nav.home') };
  if (p === '/inbox' || p.startsWith('/inbox/') || p === '/tasks' || p.startsWith('/tasks/'))
    return { entity: label('shell.nav.inbox') };
  if (p === '/library' || p === '/pages' || p === '/dashboards')
    return { entity: label('shell.nav.library') };
  if (p === '/data-quality') return { entity: label('shell.nav.dataQuality') };
  if (p === '/imports') return { entity: label('shell.title.imports') };
  if (p === '/apps') return { entity: label('shell.nav.components') };
  let m = /^\/(?:pages|decks|documents)\/([^/]+)/.exec(p);
  if (m)
    return {
      entity: recentById(decodeURIComponent(m[1]!))?.label ?? label('shell.title.page'),
      section: label('shell.nav.library'),
    };
  m = /^\/dashboards\/([^/]+)/.exec(p);
  if (m)
    return {
      entity: recentById(decodeURIComponent(m[1]!))?.label ?? label('shell.title.dashboard'),
      section: label('shell.nav.library'),
    };
  m = /^\/o\/([^/]+)(?:\/(r|new|import|forms)(?:\/([^/]+))?)?/.exec(p);
  if (m) {
    const objectKey = decodeURIComponent(m[1]!);
    const section = objectLabel(objectKey) ?? objectKey;
    if (m[2] === 'r' && m[3]) return { entity: recentById(decodeURIComponent(m[3]))?.label ?? null, section };
    if (m[2] === 'new') return { entity: label('shell.title.new'), section };
    if (m[2] === 'import') return { entity: label('shell.title.import'), section };
    if (m[2] === 'forms') return { entity: label('shell.title.form'), section };
    const view = typeof search.view === 'string' ? search.view : null;
    if (view) {
      const item = recent.find(
        (r) =>
          r.kind === 'view' &&
          r.objectKey === objectKey &&
          r.href.endsWith(`view=${encodeURIComponent(view)}`),
      );
      return { entity: item?.label ?? null, section };
    }
    return { entity: section };
  }
  if (p === '/signatures' || p.startsWith('/signatures/')) return { entity: label('shell.title.signatures') };
  if (p === '/meetings' || p.startsWith('/meetings/')) return { entity: label('shell.nav.meetings') };
  if (p === '/engagements' || p.startsWith('/engagements/')) return { entity: label('egx.nav.activities') };
  if (p === '/automations') return { entity: label('shell.nav.automations') };
  if (p.startsWith('/automations/')) {
    return {
      entity: p.includes('/runs') ? label('shell.title.runs') : label('shell.title.automation'),
      section: label('shell.nav.automations'),
    };
  }
  if (p === '/settings') return { entity: label('shell.nav.settings') };
  if (p.startsWith('/settings/') || p === '/account' || p.startsWith('/account/')) {
    const page = lookups.settingsPage?.(p) ?? null;
    const account = page ? page.account : p.startsWith('/account');
    const section = label(account ? 'shell.account' : 'shell.nav.settings');
    return page ? { entity: page.label, section } : account ? { entity: section } : { section };
  }
  if (p === '/onboarding') return { entity: label('shell.title.onboarding') };
  if (p === '/login') return { entity: label('login.title') };
  if (p === '/setup') return { entity: label('shell.title.setup') };
  if (p === '/status') return { entity: label('status.title') };
  if (p.startsWith('/dev/')) return { entity: p.slice('/dev/'.length), section: label('shell.title.dev') };
  return {};
}
